import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { TranslocoCoreModule } from "@i18n";
import { ButtonWrapperComponent } from "@vui";
import { Customer, CustomersApi, Edition, SubscriptionApi } from "../../api/proxies";
import { IMaskModule } from "angular-imask";
import { createPostalCodeMask } from "@ui/input-masks";
import { GetExternalPostalCodeResult, PostalCodeServiceServiceProxy } from "@api";

export interface SignUpPlanDialogData { }

@Component({
    selector: "sign-up-plan-dialog",
    templateUrl: "./sign-up-plan-dialog.component.html",
    styleUrls: ["./sign-up-plan-dialog.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatSlideToggleModule,
        TranslocoCoreModule,
        ButtonWrapperComponent,
        IMaskModule,
    ],
    providers: [PostalCodeServiceServiceProxy],
})
export class SignUpPlanDialogComponent implements OnInit, OnDestroy {
    protected _unsubscribeAll: Subject<any>;

    submitting: boolean = false;

    selectedPlan: number | null = null;
    showForm: boolean = false;
    signUpForm!: FormGroup;

    editions: Edition[] = [];
    planType: 'monthly' | 'annual' = 'monthly';

    planFeatures: string[] = [
        "Features.CancelAnytime",
        "Features.UpTo5Professionals",
        "Features.CreateRecords",
        "Features.ManageOrders",
        "Features.PersonalizedPage",
        "Features.ShareFiles",
        "Features.TrackAdherence",
    ];

    customerInfo: Customer | null = null;

    postalCodeMask = createPostalCodeMask();
    originalPostalCode: any;
    invalidPostalCode: boolean = false;

    private _postalCodeService = inject(PostalCodeServiceServiceProxy);

    constructor(
        public dialogRef: MatDialogRef<SignUpPlanDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: SignUpPlanDialogData,
        private fb: FormBuilder,
        private _subscriptionApi: SubscriptionApi,
        private _customersApi: CustomersApi,
    ) {
        this._unsubscribeAll = new Subject();
        this.dialogRef.addPanelClass("sign-up-plan-dialog");
        this.initializeForm();
    }

    ngOnInit() {
        this._subscriptionApi.editions(undefined).subscribe((editionsDefinition) => {
            this.editions = editionsDefinition?.editions || [];
        });
        this._customersApi.info().subscribe((customerInfo) => {
            this.customerInfo = customerInfo;
            this.fillFormWithCustomerData(customerInfo);
        });

        const initialPostalCode = this.signUpForm.get("billingCep")?.value;
        this.originalPostalCode = initialPostalCode
            ? initialPostalCode.replace(/[\\_() -]/g, "")
            : null;

        this.signUpForm
            .get("billingCep")
            ?.valueChanges.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((value) => {
                let regex = new RegExp("[\\_() -]?", "g");
                let postalCode = value ? value.replace(regex, "") : "";

                if (postalCode.length == 8) {
                    this.validateAddressPostalCode(postalCode);
                } else {
                    this.invalidPostalCode = false;
                    this.clearAddressData();
                }
            });
    }

    initializeForm(): void {
        this.signUpForm = this.fb.group({
            cardHolderName: ['', Validators.required],
            cardNumber: ['', Validators.required],
            cardExpiry: ['', Validators.required],
            cardCvc: ['', Validators.required],
            billingName: ['', Validators.required],
            billingCpfCnpj: ['', Validators.required],
            billingCep: ['', Validators.required],
            billingStreet: ['', Validators.required],
            billingNeighborhood: ['', Validators.required],
            billingCity: ['', Validators.required],
            billingState: ['', Validators.required],
            termsAccepted: [false, Validators.requiredTrue]
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    hasPendingChanges(): boolean {
        return false;
    }

    onPlanSelect(planIndex: number): void {
        this.selectedPlan = planIndex;
    }

    onContinue(): void {
        if (!this.showForm) {
            if (this.selectedPlan === null) {
                this.selectedPlan = 0;
            }
            this.showForm = true;
            this.dialogRef.addPanelClass("sign-up-plan-dialog--form");
        } else {
            if (this.signUpForm.valid) {
                this.submitting = true;

                setTimeout(() => {
                    this.submitting = false;
                    this.dialogRef.close({
                        planSelected: this.selectedPlan,
                        formData: this.signUpForm.value,
                        success: true
                    });
                }, 1000);
            } else {
                Object.keys(this.signUpForm.controls).forEach(key => {
                    this.signUpForm.get(key)?.markAsTouched();
                });
            }
        }
    }

    close(): void {
        this.dialogRef.close();
    }

    getPlanPrice(edition: Edition): number {
        if (this.planType === 'monthly') {
            return edition.price?.monthly || 0;
        } else {
            return edition.price?.annual?.once || 0;
        }
    }

    onPlanTypeChange(isAnnual: boolean): void {
        this.planType = isAnnual ? 'annual' : 'monthly';
    }

    fillFormWithCustomerData(customer: Customer): void {
        if (!customer) {
            return;
        }

        if (customer.fiscalIdentifier) {
            this.signUpForm.patchValue({
                billingCpfCnpj: customer.fiscalIdentifier
            });
        }

        if (customer.address) {
            const address = customer.address;
            this.signUpForm.patchValue({
                billingCep: address.postalCode || '',
                billingStreet: address.formattedAddress || '',
                billingNeighborhood: address.neighborhood || '',
                billingCity: address.city || '',
                billingState: address.state || ''
            });
        }
    }

    clearAddressData() {
        this.signUpForm.patchValue({
            billingStreet: null,
            billingNeighborhood: null,
            billingCity: null,
            billingState: null,
        });
    }

    validateAddressPostalCode(postalCode: string): void {
        if (postalCode === this.originalPostalCode) {
            return;
        }
        this.submitting = true;
        this._postalCodeService.validatePostalCode(postalCode).subscribe(
            (result: GetExternalPostalCodeResult) => {
                this.submitting = false;
                if (!result.code) {
                    this.invalidPostalCode = true;
                    return;
                }
                this.invalidPostalCode = false;
                this.signUpForm.patchValue({
                    billingStreet: result.address,
                    billingNeighborhood: result.neighborhood,
                    billingCity: result.city,
                    billingState: result.state,
                });
                this.signUpForm.markAsDirty();
            },
            () => {
                this.submitting = false;
                this.invalidPostalCode = true;
            },
            () => {
                this.submitting = false;
            }
        );
    }
}

